import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { WarningCircle, SignOut, WhatsappLogo, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { configApi } from "@/lib/api";

/**
 * StatusGate bloquea el acceso a la app cuando la cuenta NO está Activa.
 * - Permite login (el usuario ve la campanita con la razón).
 * - Muestra un panel bloqueante con el motivo y acciones (WhatsApp / cerrar sesión).
 * - Cuando el super admin reactiva la cuenta, /auth/me devuelve status=Activa
 *   y el bloqueo desaparece automáticamente (basta con refrescar).
 */
export default function StatusGate({ children }) {
  const { user, logout } = useAuth();
  const [supportWsp, setSupportWsp] = useState("");

  useEffect(() => {
    // Cargar el número de WhatsApp de soporte configurado en backend (env ADMIN_WHATSAPP).
    configApi.public().then((c) => setSupportWsp(c?.support_whatsapp || "")).catch(() => {});
  }, []);

  if (!user) return children;
  const st = user.status || "Activa";
  if (st === "Activa") return children;

  const wsp = supportWsp || "0999725015"; // fallback si aún no cargó config

  const copy = {
    Pendiente: {
      title: "Cuenta en verificación",
      body:
        "Estamos revisando tu registro. Recibirás acceso completo en un máximo de 24 horas hábiles.",
      icon: Clock,
      color: "text-amber-600",
      bg: "bg-amber-50 border-amber-200",
    },
    Suspendida: {
      title: "Cuenta suspendida",
      body:
        "Tu cuenta está suspendida temporalmente. Contáctanos vía WhatsApp para reactivarla y continuar operando en Recobro.",
      icon: WarningCircle,
      color: "text-destructive",
      bg: "bg-destructive/5 border-destructive/30",
    },
    Bloqueada: {
      title: "Cuenta bloqueada",
      body:
        "Tu cuenta ha sido bloqueada. Contáctanos por WhatsApp para conocer los pasos a seguir.",
      icon: WarningCircle,
      color: "text-destructive",
      bg: "bg-destructive/5 border-destructive/30",
    },
  }[st] || {
    title: `Cuenta ${st}`,
    body: "Tu cuenta no está activa. Contacta al equipo de Recobro para reactivarla.",
    icon: WarningCircle,
    color: "text-destructive",
    bg: "bg-destructive/5 border-destructive/30",
  };

  const Icon = copy.icon;

  const handleLogout = async () => {
    await logout();
    toast.success("Sesión cerrada");
  };

  const trialLine =
    st === "Suspendida" && user.trial_until
      ? `Tu periodo de prueba finalizó el ${user.trial_until}.`
      : null;

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6" data-testid="status-gate">
      <div className={`max-w-lg w-full border rounded-lg p-8 ${copy.bg}`}>
        <div className="flex flex-col items-center text-center">
          <Icon size={56} weight="duotone" className={copy.color} />
          <h1 className="font-heading text-2xl font-semibold mt-4" data-testid="status-gate-title">
            {copy.title}
          </h1>
          {trialLine && (
            <p className="text-xs text-muted-foreground mt-2 font-mono">{trialLine}</p>
          )}
          <p className="text-sm text-foreground/80 mt-3">{copy.body}</p>

          <div className="mt-6 flex flex-col sm:flex-row gap-2 w-full">
            <Button
              asChild
              className="flex-1"
              data-testid="status-gate-whatsapp"
            >
              <a
                href={(() => {
                  // Normaliza el número EC: quita todo no dígito y anteponer 593 si empieza con 0.
                  const digits = String(wsp || "").replace(/[^0-9]/g, "");
                  const intl = digits.startsWith("593")
                    ? digits
                    : digits.startsWith("0")
                    ? "593" + digits.slice(1)
                    : digits;
                  return `https://wa.me/${intl}?text=${encodeURIComponent(
                    `Hola, mi cuenta Recobro (${user.email}) está ${st.toLowerCase()}.`
                  )}`;
                })()}
                target="_blank"
                rel="noopener noreferrer"
              >
                <WhatsappLogo size={16} weight="bold" className="mr-2" />
                Contactar por WhatsApp
              </a>
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="status-gate-logout">
              <SignOut size={16} weight="bold" className="mr-2" />
              Cerrar sesión
            </Button>
          </div>

          <p className="text-[11px] text-muted-foreground mt-6">
            Cuando el super administrador reactive tu cuenta, podrás usar Recobro con normalidad al recargar la página.
          </p>
        </div>
      </div>
    </div>
  );
}
